import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import { createUserIdentifierFromActionPageKey } from '@/ui/injectionKeys';
import { defineComponent, h, ref, watch, provide } from 'vue';
import { Modal } from '@/ui/components/Modal';
import { loader, pageSubtitle } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { useTask } from '@/ui/concerns';
import {
  VDataComponents,
  sequence,
  filterListToEditableGroups,
  filterListToEditableUsers,
  filterListToOptionsUsers,
} from '@/ui/helpers';
import { filter as userFilter, filter as getAllUsers } from '@/services/api/resources/user';
import { Button } from '@/ui/components/Button';
import cloneDeep from 'lodash/cloneDeep';
import { emptyIssueTemplateBuilder, IssueTemplateBuilderEditable } from '@/ui/DTO/issueTemplateBuilderEditable';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { CREATE_ACTION_MODAL_WIDTH } from '@/ui/components/modals/constants';
import { Permissions } from '@/ui/auth/auth.types';
import { kebabCase } from 'lodash';
import { useAssignees } from '@/ui/concerns/useAssignees';
import IssueTemplateBuilder from '@/ui/components/checklist/IssueTemplateBuilder.vue';
import { useIssueTemplate } from '@/ui/concerns/issue/useIssueTemplate';

interface IssueTemplateCreateModalProps {
  issue?: IssueTemplateBuilderEditable | null;
}

export const IssueCreatorModal = defineComponent<IssueTemplateCreateModalProps>({
  name: 'IssueCreatorModal',
  props: ['active', 'issue'],
  setup(props, context) {
    provide(createUserIdentifierFromActionPageKey, CreateUserModelIdentifier.actionCreator);
    const assigneeApprovalOptions = ref<SelectOptions<string>>([]);

    const issueEditable = ref(props.issue ? cloneDeep(props.issue) : emptyIssueTemplateBuilder());

    const closeModal = () => {
      issueEditable.value = emptyIssueTemplateBuilder();
      context.emit('close');
    };

    const { showValidationErrors, hasNoValidationErrors } = useIssueTemplate(issueEditable);

    const emitSave = () => {
      issueEditable.value.assignees.value = filterListToEditableUsers(issueEditable.value.assignees.value);
      issueEditable.value.associateAssignees.value = filterListToEditableUsers(issueEditable.value.associateAssignees.value);
      issueEditable.value.assignedGroups.value = filterListToEditableGroups(issueEditable.value.assignedGroups.value);

      context.emit('save', cloneDeep(issueEditable.value));

      issueEditable.value = emptyIssueTemplateBuilder();
      showValidationErrors.value = false;
    };

    const onSaveClick = () => {
      showValidationErrors.value = true;

      // The only field to validate in this form is the `title`.
      // If the user selects that the `title` should be defined
      // by the inspector there won't be anything to validate.
      if (issueEditable.value.title.definedByInspector) {
        emitSave();

        return;
      }

      if (hasNoValidationErrors.value) {
        emitSave();
      }
    };

    const {
      assigneeOptions,
      fillAssigneeOptions,
      fillGroupsOptions,
    } = useAssignees();

    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllGroups()),
      ([users, groups]) => {
        fillGroupsOptions(groups);
        fillAssigneeOptions(users);
        getActionApprovalUsers.run();
      },
    );

    const getActionApprovalUsers = useTask(
      () => userFilter({
        permission: [Permissions.APPROVE_ALL_ACTIONS, Permissions.APPROVE_ASSIGNED_ACTIONS].map(kebabCase),
      }),
      response => {
        assigneeApprovalOptions.value = [
          ...filterListToOptionsUsers(response.data),
        ];
      },
    );

    const onIssueTemplatePropChange = (resource: IssueTemplateBuilderEditable | null | undefined) => {
      issueEditable.value = cloneDeep(resource) || emptyIssueTemplateBuilder();
    };

    watch(() => props.issue, onIssueTemplatePropChange);

    return function render() {
      return h(Modal, {
        props: {
          ...props,
          active: true,
          width: CREATE_ACTION_MODAL_WIDTH,
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        on: {
          close: closeModal,
        },
        scopedSlots: {
          actions: () => h('div', [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('modal.cancel')),
            h(Button, {
              props: {
                loading: false,
                disabled: false,
              },
              on: { click: onSaveClick },
            }, t('common.save')),
          ]),
        },
      }, [
        loader('div', getFilters, [
          pageSubtitle(t('issue-template.issue')),
          h(IssueTemplateBuilder, {
            props: {
              record: issueEditable.value,
              assigneeOptions: assigneeOptions.value,
              assigneeApprovalOptions: assigneeApprovalOptions.value,
            },
          }),
        ], true, 'issue-creator-modal'),
      ]);
    };
  },
});

export function issueCreatorModal(data: VDataComponents<IssueTemplateCreateModalProps>) {
  return h(IssueCreatorModal, data);
}
